/**
 * Authentication service for Crossover API integration
 * Handles login, token management, and user profile retrieval
 */

import { AuthToken, CrossoverUserProfile } from '../types';

// Constants
const TOKEN_STORAGE_KEY = 'crossover_auth_token';
const PROFILE_STORAGE_KEY = 'crossover_user_profile';
const BASE_URL = 'https://api.crossover.com';

/**
 * Authentication error class for better error handling
 */
export class AuthError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * Authenticate user with Crossover API using Basic Auth
 * @param username - User's email/username
 * @param password - User's password
 * @returns Promise<AuthToken> - Authentication token
 */
export const login = async (username: string, password: string): Promise<AuthToken> => {
  try {
    // Create Basic Auth header
    const credentials = btoa(`${username}:${password}`);
    
    const response = await fetch(`${BASE_URL}/api/v3/token`, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new AuthError('Invalid credentials', 401);
      } else if (response.status === 403) {
        throw new AuthError('Access forbidden', 403);
      } else {
        throw new AuthError(`Authentication failed: ${response.statusText}`, response.status);
      }
    }

    const data: AuthToken = await response.json();
    
    if (!data.token) {
      throw new AuthError('No token received from server');
    }

    // Store token in localStorage for persistence
    localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
    
    return data;
  } catch (error) {
    console.error('Login error:', error);
    
    if (error instanceof AuthError) {
      throw error;
    }
    
    // Network or other errors
    throw new AuthError('Network error or server unavailable');
  }
};

/**
 * Get user profile and manager information
 * @param token - Authentication token
 * @returns Promise<CrossoverUserProfile> - User profile with manager ID
 */
export const getUserProfile = async (token: string): Promise<CrossoverUserProfile> => {
  try {
    const response = await fetch(`${BASE_URL}/api/identity/users/current/detail`, {
      method: 'GET',
      headers: {
        'X-Auth-Token': token,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired or invalid
        logout();
        throw new AuthError('Session expired, please log in again', 401);
      } else {
        throw new AuthError(`Failed to fetch user profile: ${response.statusText}`, response.status);
      }
    }

    const profile: CrossoverUserProfile = await response.json();
    
    // Note: Manager permissions are not required for basic access
    // Manager validation is handled at the feature level (My Team, Central PS)
    // All authenticated users can access the Basic view

    // Store profile in localStorage
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
    
    return profile;
  } catch (error) {
    console.error('Get user profile error:', error);
    
    if (error instanceof AuthError) {
      throw error;
    }
    
    throw new AuthError('Failed to retrieve user profile');
  }
};

/**
 * Get stored authentication token
 * @returns string | null - Stored token or null if not found
 */
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

/**
 * Get stored user profile
 * @returns CrossoverUserProfile | null - Stored profile or null if not found
 */
export const getStoredProfile = (): CrossoverUserProfile | null => {
  const profileJson = localStorage.getItem(PROFILE_STORAGE_KEY);
  if (!profileJson) return null;
  
  try {
    return JSON.parse(profileJson) as CrossoverUserProfile;
  } catch (error) {
    console.error('Error parsing stored profile:', error);
    localStorage.removeItem(PROFILE_STORAGE_KEY);
    return null;
  }
};

/**
 * Check if user is authenticated
 * @returns boolean - True if authenticated token exists
 */
export const isAuthenticated = (): boolean => {
  const token = getStoredToken();
  return token !== null && token.length > 0;
};

/**
 * Logout user by clearing stored data
 */
export const logout = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(PROFILE_STORAGE_KEY);
};

/**
 * Validate token by making a test API call
 * @param token - Token to validate
 * @returns Promise<boolean> - True if token is valid
 */
export const validateToken = async (token: string): Promise<boolean> => {
  try {
    await getUserProfile(token);
    return true;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
}; 